import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PropertySimple {
    id: number;
    title: string;
    price: number;
    area_sqm: number;
    image_url?: string;
    area_name?: string;
    bedrooms: number;
    bathrooms: number;
    property_type: string;
    finishing_status: string;
    is_installment_eligible: boolean;
    down_payment_percentage?: number;
    installment_years?: number;
    developer_name?: string;
    compound_name?: string;
    amenities?: any[];
    listing_type?: string;
    delivery_date?: string;
    is_compound?: boolean;
}

interface ComparisonContextType {
    selectedProperties: PropertySimple[];
    addToCompare: (property: any, isCompound?: boolean) => void;
    removeFromCompare: (id: number) => void;
    isInCompare: (id: number) => boolean;
    clearComparison: () => void;
    updateProperty: (property: PropertySimple) => void;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

export const ComparisonProvider = ({ children }: { children: ReactNode }) => {
    const [selectedProperties, setSelectedProperties] = useState<PropertySimple[]>([]);

    // Load from local storage on mount
    useEffect(() => {
        const saved = localStorage.getItem('protech_compare');
        if (saved) {
            try {
                setSelectedProperties(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to load comparison data", e);
            }
        }
    }, []);

    // Save to local storage on change
    useEffect(() => {
        localStorage.setItem('protech_compare', JSON.stringify(selectedProperties));
    }, [selectedProperties]);

    const addToCompare = (property: any, isCompound: boolean = false) => {

        
        // Normalize property data structure if needed
        const normalized: PropertySimple = {
            id: property.id,
            title: property.title || property.name,
            price: property.price || property.start_price || 0,
            area_sqm: property.area_sqm || 0,
            // Robustly find the image URL
            image_url: property.images?.[0]?.image_url || property.images?.[0]?.url || property.image_url || property.compound?.image_url || property.launch_image_url,
            area_name: property.area?.name || property.area_name,
            bedrooms: property.bedrooms || 0,
            bathrooms: property.bathrooms || 0,
            property_type: property.property_type || 'Compound',
            finishing_status: property.finishing_status,
            is_installment_eligible: property.is_installment_eligible,
            down_payment_percentage: property.down_payment_percentage,
            installment_years: property.installment_years,
            developer_name: property.developer?.name,
            compound_name: property.compound?.name || (isCompound ? property.name : undefined),
            amenities: property.amenities,
            listing_type: property.is_resale ? 'Resale' : 'Developer Sale',
            delivery_date: property.availability_date || property.compound?.delivery_date || property.delivery_date,
            is_compound: isCompound || !!property.start_price
        };

        if (!selectedProperties.find(p => p.id === normalized.id)) {
            setSelectedProperties(prev => [...prev, normalized]);
        }
    };

    const updateProperty = (property: PropertySimple) => {
        setSelectedProperties(prev => prev.map(p => p.id === property.id ? property : p));
    };

    const removeFromCompare = (id: number) => {
        setSelectedProperties(prev => prev.filter(p => p.id !== id));
    };

    const isInCompare = (id: number) => {
        return selectedProperties.some(p => p.id === id);
    };

    const clearComparison = () => setSelectedProperties([]);

    return (
        <ComparisonContext.Provider value={{ selectedProperties, addToCompare, updateProperty, removeFromCompare, isInCompare, clearComparison }}>
            {children}
        </ComparisonContext.Provider>
    );
};

export const useComparison = () => {
    const context = useContext(ComparisonContext);
    if (!context) throw new Error('useComparison must be used within ComparisonProvider');
    return context;
};
